/**
 * DEFCAT-R2-07 — external-IDs editor for a catalogue entry.
 *
 * The taxonomy admin API stores `externalIds` as an ARRAY of
 * `{provider, id}` with replace-list semantics (the full array overwrites;
 * explicit null clears) — distinct from the flat `{provider: id}` map used by
 * Sites / Contract-Positions. This editor manages the array shape directly.
 *
 * UX mirrors `contract-positions/ExternalIdsEditor.tsx`: a repeating list of
 * `[provider | id | ⊖]` rows + an "+ Add external ID" button.
 */
import { type ReactElement } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { typography } from "@aeriosiq/ui";
import type { CatalogueEntryExternalId } from "@aeriosiq/types";
import { CircleMinusIcon, PlusIcon } from "@aeriosiq/ui/icons";

/**
 * Section-heading style matching the analysis filter widget's `LevelHeader`
 * (the platform's canonical compact-widget scale).
 */
const sectionHeadingSx = {
  fontSize: typography.size["label-md"],
  fontWeight: typography.weight.semibold,
  letterSpacing: typography.tracking["label-md"],
  color: "text.secondary",
  textTransform: "uppercase" as const,
  display: "block",
  mb: 0.75,
};

/** Dense field font — caption-lg (13), filter-widget body scale. */
const compactFieldSx = {
  "& .MuiInputBase-input": { fontSize: typography.size["caption-lg"] },
};

export interface CatalogueExternalIdsEditorProps {
  value: CatalogueEntryExternalId[];
  onChange: (next: CatalogueEntryExternalId[]) => void;
  disabled?: boolean;
}

/**
 * Drop empty rows + trim. Returns the array to send as the replace-list PATCH
 * value; an empty array clears all external IDs on the entry.
 */
export function cleanExternalIdRows(
  rows: CatalogueEntryExternalId[],
): CatalogueEntryExternalId[] {
  const out: CatalogueEntryExternalId[] = [];
  for (const r of rows) {
    const provider = r.provider.trim();
    const id = r.id.trim();
    if (provider.length === 0 || id.length === 0) continue;
    out.push({ provider, id });
  }
  return out;
}

export function CatalogueExternalIdsEditor({
  value,
  onChange,
  disabled = false,
}: CatalogueExternalIdsEditorProps): ReactElement {
  function update(
    index: number,
    patch: Partial<CatalogueEntryExternalId>,
  ): void {
    const next = [...value];
    const current = next[index];
    if (!current) return;
    next[index] = { ...current, ...patch };
    onChange(next);
  }

  function remove(index: number): void {
    onChange(value.filter((_, i) => i !== index));
  }

  function append(): void {
    onChange([...value, { provider: "", id: "" }]);
  }

  return (
    <Box>
      <Typography component="span" sx={sectionHeadingSx}>
        External IDs
      </Typography>
      <Stack spacing={0.5}>
        {value.length === 0 ? (
          <Typography
            color="text.disabled"
            sx={{
              fontStyle: "italic",
              fontSize: typography.size["caption-lg"],
            }}
          >
            No external IDs.
          </Typography>
        ) : (
          value.map((row, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={0.5}
              alignItems="center"
            >
              <TextField
                value={row.provider}
                onChange={(e) => {
                  update(index, { provider: e.target.value });
                }}
                placeholder="Provider"
                size="small"
                sx={{ width: 180, ...compactFieldSx }}
                disabled={disabled}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} provider`,
                  },
                }}
              />
              <TextField
                value={row.id}
                onChange={(e) => {
                  update(index, { id: e.target.value });
                }}
                placeholder="ID"
                size="small"
                sx={{ flex: 1, ...compactFieldSx }}
                disabled={disabled}
                slotProps={{
                  htmlInput: {
                    "aria-label": `External ID ${String(index + 1)} value`,
                  },
                }}
              />
              {!disabled ? (
                <IconButton
                  aria-label={`Remove external ID ${String(index + 1)}`}
                  size="small"
                  sx={{ p: 0.5 }}
                  onClick={() => {
                    remove(index);
                  }}
                >
                  <CircleMinusIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
            </Stack>
          ))
        )}
      </Stack>
      {!disabled ? (
        <Button
          size="small"
          variant="text"
          startIcon={<PlusIcon sx={{ fontSize: 16 }} />}
          onClick={append}
          sx={{ mt: 0.75, fontSize: typography.size["caption-lg"] }}
        >
          Add external ID
        </Button>
      ) : null}
    </Box>
  );
}
